import { useState, useRef, useEffect, useCallback, useLayoutEffect } from 'react';
import { dispatchFabricEvent, useFabricEvent } from '../../hooks/useFabricEvent.js';
import { isString, isDefined } from '@fabric-app/utils/guards';
import { useMutationDeleteComment } from '../../comments/mutations/useMutationDeleteComment.js';
import { getCombinedRect, getNearestScrollableParent } from '@fabric-app/utils/element';
import { FloatingActions } from '../components/FloatingActions.js';
import { DOMModel, colorToRGBStyle } from '../classes/DOMModel.js';
import { randomString } from '@fabric-app/utils/random';
import { cssVar } from '@fabric-app/ui/theme/variables';
import { useQueryComments } from '../../comments/queries/useQueryComments.js';
import { useMutationCreateComment } from '../../comments/mutations/useMutationCreateComment.js';
import { animationAnnotationFocusCss } from '../constants/animations.js';
import { ANNOTATION_DEFAULT_COLOR } from '../config.js';

const annotationCSS = ({ annotatedId, hoverId, focusId }) => `
  @keyframes annotation-focus {
    ${animationAnnotationFocusCss}
  }

  [data-annotated='${annotatedId}'] span[data-annotation-placeholder],
  [data-annotated='${annotatedId}'] span[data-annotation] {
    font-size: inherit;
    color: inherit;
    min-height: inherit;
    font-family: inherit;
  }


  [data-annotated='${annotatedId}'] span[data-annotation-placeholder] {
    opacity: 1;

    background-color: rgba(var(--annotation-color, ${cssVar["color-bg-annotation-rgb"]}), 0.3);
    box-shadow: none;
  }

  [data-annotated='${annotatedId}'] span[data-annotation] {
    opacity: 1;

    background-color: rgba(var(--annotation-color, ${cssVar["color-bg-annotation-rgb"]}), 0.3);
    box-shadow: none;
  }

  [data-annotated='${annotatedId}'] span[data-annotation]:hover {
    background-color: rgba(var(--annotation-color, ${cssVar["color-bg-annotation-rgb"]}), 0.3);
    box-shadow: none;
  }

  ${isDefined(hoverId) ? `
  [data-annotated='${annotatedId}'] span[data-annotation][data-comment-ids*='${String(hoverId)}'] {
    background-color: rgba(var(--annotation-color, ${cssVar["color-bg-annotation-rgb"]}), 0.5);
    box-shadow: none;
  }
    ` : ""}

  ${isDefined(focusId) ? `
  [data-annotated='${annotatedId}'] span[data-annotation][data-comment-ids*='${String(focusId)}'] {
    animation: annotation-focus 0.3s ease 0s 1 normal none;
  }
  ` : ""}
`;
const useSelectionBasedAnnotations = ({ doc, enabled, resourceId, domModelOptions, scrollToChunk, scrollToElement }) => {
  const [model, setModel] = useState(null);
  const { comments } = useQueryComments(resourceId);
  const domModelOptionsRef = useRef(domModelOptions);
  domModelOptionsRef.current = domModelOptions;
  useEffect(() => {
    if (!doc || !enabled) return;
    const model2 = new DOMModel(doc, domModelOptionsRef.current);
    setModel(model2);
    return () => {
      model2.restore();
    };
  }, [
    doc,
    enabled,
    domModelOptionsRef
  ]);
  useEffect(() => {
    if (!model || !comments) return;
    model.setComments(comments);
  }, [
    comments,
    model
  ]);
  const modelRef = useRef(model);
  modelRef.current = model;
  const onAnnotationCreation = useCallback(() => {
    if (!modelRef.current || !resourceId) return;
    const widget = modelRef.current.getBaseWidgetFromSelection();
    if (!widget) return;
    const annotation = {
      id: widget.id,
      text: {
        color: ANNOTATION_DEFAULT_COLOR,
        text: widget.text,
        locationStart: widget.locationStart,
        locationEnd: widget.locationEnd,
        chunkSize: modelRef.current.getChunkSize(),
        startChunk: widget.startChunk,
        endChunk: widget.endChunk
      }
    };
    modelRef.current.addWidget({
      ...widget,
      attributes: {
        "data-annotation-placeholder": "true"
      },
      styles: {
        ...colorToRGBStyle(annotation.text.color)
      },
      meta: {
        resourceId,
        isPlaceholder: true
      }
    });
    dispatchFabricEvent("fabric-annotation-action-create", {
      type: "create-annotation",
      annotation,
      resourceId
    });
  }, [
    resourceId
  ]);
  const { mutate: createComment } = useMutationCreateComment();
  const onHighlightCreation = useCallback(() => {
    if (!modelRef.current || !resourceId) return;
    const widget = modelRef.current.getBaseWidgetFromSelection();
    if (!widget) return;
    const annotation = {
      id: widget.id,
      text: {
        color: ANNOTATION_DEFAULT_COLOR,
        text: widget.text,
        locationStart: widget.locationStart,
        locationEnd: widget.locationEnd,
        chunkSize: modelRef.current.getChunkSize(),
        startChunk: widget.startChunk,
        endChunk: widget.endChunk
      }
    };
    createComment({
      resourceId,
      content: "",
      annotation
    });
    const selection = document.getSelection();
    if (selection) {
      selection.removeAllRanges();
    }
  }, [
    resourceId,
    createComment
  ]);
  const [hoverCommentId, setHoverCommentId] = useState(null);
  const hoverCommentIdRef = useRef(null);
  const toolbarDOMRef = useRef(null);
  const hoverTimeout = useRef(null);
  const { mutate: deleteComment } = useMutationDeleteComment();
  const [annotatedId] = useState(`fabric-annotation-${randomString(8)}`);
  const styleTagRef = useRef(null);
  const updateStyling = useCallback((hoverId, focusId) => {
    if (!doc || !enabled) return;
    if (!styleTagRef.current) return;
    styleTagRef.current.innerHTML = annotationCSS({
      annotatedId,
      hoverId,
      focusId
    });
  }, [
    doc,
    enabled,
    annotatedId
  ]);
  useLayoutEffect(() => {
    if (!doc || !enabled) return;
    const styleTag = document.createElement("style");
    doc.appendChild(styleTag);
    doc.setAttribute("data-annotated", annotatedId);
    styleTagRef.current = styleTag;
    updateStyling();
    return () => {
      doc.removeChild(styleTag);
      styleTagRef.current = null;
      doc.removeAttribute("data-annotated");
    };
  }, [
    doc,
    enabled,
    annotatedId,
    updateStyling
  ]);
  const onAnnotationHover = useCallback((commentId, dispatch = true) => {
    if (hoverTimeout.current) window.clearTimeout(hoverTimeout.current);
    hoverTimeout.current = null;
    if (hoverCommentIdRef.current === commentId) return;
    setHoverCommentId(commentId);
    hoverCommentIdRef.current = commentId;
    updateStyling(commentId);
    if (resourceId && dispatch) {
      dispatchFabricEvent("fabric-annotation-action-hover", {
        type: "hover-annotation",
        hover: {
          commentId
        },
        origin: "content",
        resourceId
      });
    }
  }, [
    resourceId,
    updateStyling
  ]);
  const onAnnotationLeave = useCallback((dispatch = true) => {
    if (hoverTimeout.current) return;
    if (hoverCommentIdRef.current === null) return;
    hoverTimeout.current = window.setTimeout(() => {
      setHoverCommentId(null);
      hoverCommentIdRef.current = null;
      updateStyling(null);
      if (dispatch && resourceId) {
        dispatchFabricEvent("fabric-annotation-action-hover", {
          type: "hover-annotation",
          hover: null,
          origin: "content",
          resourceId
        });
      }
    }, 1e3);
  }, [
    resourceId,
    updateStyling
  ]);
  useFabricEvent("fabric-annotation-action-hover", (e) => {
    const { hover, origin, resourceId: eventResourceId } = e.detail;
    if (resourceId !== eventResourceId || origin === "content") return;
    if (hover === null || !hover.commentId) {
      onAnnotationLeave();
    } else if (hover && hover.commentId) {
      onAnnotationHover(hover.commentId);
    }
  });
  useEffect(() => {
    if (!doc) return;
    const onPointerMove = (event) => {
      const commentIds = event.target instanceof HTMLElement ? event.target.closest("[data-comment-ids]")?.getAttribute("data-comment-ids") : null;
      const commentId = commentIds ? commentIds.split(",")[0] : null;
      if (!commentId) {
        onAnnotationLeave();
        return;
      }
      onAnnotationHover(commentId);
    };
    const onPointerLeave = () => {
      onAnnotationLeave();
    };
    doc.addEventListener("pointermove", onPointerMove);
    doc.addEventListener("pointerdown", onPointerMove);
    doc.addEventListener("pointerleave", onPointerLeave);
    return () => {
      doc.removeEventListener("pointermove", onPointerMove);
      doc.removeEventListener("pointerdown", onPointerMove);
      doc.removeEventListener("pointerleave", onPointerLeave);
    };
  }, [
    doc,
    onAnnotationHover,
    onAnnotationLeave
  ]);
  const updateToolbar = useCallback(() => {
    if (!doc || !hoverCommentId) {
      if (toolbarDOMRef.current) {
        toolbarDOMRef.current.remove();
        toolbarDOMRef.current = null;
      }
      return;
    }
    const elements = doc.querySelectorAll(`span[data-comment-ids*="${hoverCommentId}"]`);
    const combinedRects = getCombinedRect(Array.from(elements));
    if (!combinedRects) {
      if (toolbarDOMRef.current) {
        toolbarDOMRef.current.remove();
        toolbarDOMRef.current = null;
      }
      return;
    }
    if (!toolbarDOMRef.current) {
      toolbarDOMRef.current = document.createElement("div");
      toolbarDOMRef.current.innerHTML = FloatingActions.Render;
      const commentButton = toolbarDOMRef.current.querySelector('[data-annotation-action="comment"]');
      const deleteButton = toolbarDOMRef.current.querySelector('[data-annotation-action="delete"]');
      if (!commentButton || !deleteButton) {
        toolbarDOMRef.current.remove();
        toolbarDOMRef.current = null;
        return;
      }
      document.body.appendChild(toolbarDOMRef.current);
      commentButton.addEventListener("click", () => {
        if (!resourceId) return;
        dispatchFabricEvent("fabric-annotation-action-focus", {
          type: "focus-annotation",
          commentId: hoverCommentId,
          origin: "content",
          resourceId,
          annotation: {}
        });
      });
      deleteButton.addEventListener("click", () => {
        if (!resourceId || !hoverCommentId) return;
        setHoverCommentId(null);
        deleteComment({
          commentId: hoverCommentId,
          resourceId
        });
      });
      toolbarDOMRef.current.addEventListener("mouseenter", () => {
        if (!toolbarDOMRef.current) return;
        const commentId = toolbarDOMRef.current.dataset.commentId;
        if (!commentId) return;
        onAnnotationHover(commentId);
      });
      toolbarDOMRef.current.addEventListener("mouseleave", () => {
        onAnnotationLeave();
      });
      toolbarDOMRef.current.addEventListener("pointerdown", (e) => {
        e.stopPropagation();
      });
    }
    toolbarDOMRef.current.style.position = "fixed";
    toolbarDOMRef.current.style.left = `${combinedRects.left + combinedRects.width / 2}px`;
    toolbarDOMRef.current.style.top = `${combinedRects.top}px`;
    toolbarDOMRef.current.style.transform = "translate(-50%, calc(-100% - 20px))";
    toolbarDOMRef.current.style.zIndex = "125";
    toolbarDOMRef.current.style.pointerEvents = "auto";
    toolbarDOMRef.current.dataset.annotationActions = "true";
    toolbarDOMRef.current.dataset.commentId = hoverCommentId;
  }, [
    doc,
    hoverCommentId,
    resourceId,
    deleteComment,
    onAnnotationHover,
    onAnnotationLeave
  ]);
  useEffect(() => {
    updateToolbar();
    return () => {
      if (toolbarDOMRef.current) {
        toolbarDOMRef.current.remove();
        toolbarDOMRef.current = null;
      }
    };
  }, [
    hoverCommentId,
    updateToolbar
  ]);
  useEffect(() => {
    if (!hoverCommentId || !toolbarDOMRef.current) return;
    const element = doc;
    if (!element) return;
    const scrollParent = getNearestScrollableParent(element);
    if (!scrollParent) return;
    const onScroll = () => {
      updateToolbar();
    };
    scrollParent.addEventListener("scroll", onScroll);
    return () => {
      scrollParent.removeEventListener("scroll", onScroll);
    };
  }, [
    updateToolbar,
    hoverCommentId,
    doc
  ]);
  useFabricEvent("fabric-annotation-action-focus", (e) => {
    const { commentId, origin, resourceId: eventResourceId, annotation } = e.detail;
    if (resourceId !== eventResourceId || origin === "content" || !commentId || isString(annotation) || !annotation.text || !isDefined(annotation.text.chunkSize) || !isDefined(annotation.text.endChunk) || !isDefined(annotation.text.startChunk)) return;
    onAnnotationHover(commentId, false);
    if (!doc) return;
    updateStyling(hoverCommentIdRef.current, null);
    requestAnimationFrame(() => {
      updateStyling(hoverCommentIdRef.current, commentId);
    });
    doc.focus();
    const chunkIndex = annotation.text.startChunk;
    const chunkRendered = !!modelRef.current?.chunks[chunkIndex];
    if (!chunkRendered) scrollToChunk?.(chunkIndex);
    const locationStart = annotation.text.locationStart;
    const scrollToNode = () => {
      const segment = modelRef.current?.findSegmentInChunk(chunkIndex, locationStart);
      if (!segment) return;
      const node = segment.node;
      if (!node) return;
      const element = node instanceof HTMLElement ? node : node.parentElement;
      if (!element) return;
      if (scrollToElement) {
        scrollToElement(element);
      } else {
        element.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "center"
        });
      }
    };
    if (domModelOptions?.mode === "dynamic" && !chunkRendered) {
      let tries = 0;
      const retry = () => {
        if (tries > 20) return;
        tries++;
        if (modelRef.current?.chunks[chunkIndex]) {
          scrollToNode();
        } else {
          setTimeout(retry, 20);
        }
      };
      retry();
    } else {
      scrollToNode();
    }
  });
  return {
    model,
    onAnnotationCreation,
    onHighlightCreation
  };
};

export { useSelectionBasedAnnotations };
//# sourceMappingURL=useSelectionBasedAnnotations.js.map
